import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';
import {
    SummaryGrid,
    SummaryGroup,
    SummaryHeader,
    SummaryRow,
    SummarySection,
    SummaryText,
} from '../../../../components';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

import type { IClaim, IClaimInit } from '../../../../types';
import type { FC } from 'react';

interface IDamageDetailsProps {
    claim?: IClaimInit | IClaim | null;
    setStep: (step: number) => void;
}

export const DamageDetailsSummary: FC<IDamageDetailsProps> = ({ claim, setStep }) => {
    const formContext = useFormContext();
    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    // DAMAGE DESCRIPTION
    const detailsOfDamageLoss: string = formContext.getValues(claimFields.detailsOfDamageLoss);
    const subAffected: number = formContext.getValues(claimFields.estimatedSubsAffected) ?? '';
    const subAffectedDisplay = subAffected ? subAffected : localization.translate('dropdownOptions.na');
    const outageDuration: string = formContext.getValues(claimFields.outageDuration) ?? '';
    const outageDurationDisplay =
        outageDuration.length > 0 ? outageDuration : localization.translate('dropdownOptions.na');
    const activityType: string =
        claim?.damageActivityTypeList?.find((a) => a.id === formContext.getValues(claimFields.damageActivityType))
            ?.name ?? localization.translate('dropdownOptions.na');
    const damageBy: string =
        claim?.damageByTypeList?.find((a) => a.id === formContext.getValues(claimFields.damageByType))?.name ??
        localization.translate('dropdownOptions.na');

    // FACILITIES
    const facilities: string = formContext
        .getValues(claimFields.damageFacilityTypes)
        ?.map((f: number) => claim?.damageFacilityTypeList.find((ft) => Number(ft.id) === f)?.name ?? '')
        ?.join(', ');

    // LOCATES
    const locateAccuracy: string =
        claim?.locateAccuracyList?.find((c) => c.id === formContext.getValues(claimFields.locateAccuracy))?.label ??
        localization.translate('dropdownOptions.na');
    const locateMarkingFlags: string = formContext.getValues(claimFields.locateMarkingFlags)
        ? `${localization.translate('locateMarkings.flagsLabel')}, `
        : '';
    const locateMarkingPaint: string = formContext.getValues(claimFields.locateMarkingPaint)
        ? `${localization.translate('locateMarkings.paintLabel')}, `
        : '';
    const locateMarkingStakes: string = formContext.getValues(claimFields.locateMarkingStakes)
        ? `${localization.translate('locateMarkings.stakesLabel')}, `
        : '';
    const locateMarkingUnknown: string = formContext.getValues('isMarkingUnknown')
        ? localization.translate('dropdownOptions.unknown')
        : '';
    const locateMarkings: string = locateMarkingFlags + locateMarkingPaint + locateMarkingStakes + locateMarkingUnknown;
    const locateMarkingDisplay: string =
        locateMarkings.length > 0 ? locateMarkings : localization.translate('dropdownOptions.na');
    const isLocatesHidden: boolean = formContext.getValues('isLocatesHidden') ?? false;
    const hasLocateDetails: string = !isLocatesHidden
        ? formContext.getValues(claimFields.wereLocatesPresent)
            ? localization.translate('dropdownOptions.yes')
            : localization.translate('dropdownOptions.no')
        : localization.translate('dropdownOptions.na');

    // SPAN REPLACEMENT
    const isSpanReplacement: string = formContext.getValues(claimFields.isSpanReplacementNeeded)
        ? localization.translate('dropdownOptions.yes')
        : localization.translate('dropdownOptions.no');
    const isSpanUgInConduit: string = formContext.getValues(claimFields.wasDamageUnderground)
        ? formContext.getValues(claimFields.cableInConduit)
            ? localization.translate('dropdownOptions.yes')
            : localization.translate('dropdownOptions.no')
        : localization.translate('dropdownOptions.na');
    const otherMaterial: string =
        formContext.getValues(claimFields.cableSize)?.length > 0
            ? formContext.getValues(claimFields.cableSize)
            : localization.translate('dropdownOptions.na');
    const spanFootage: string = formContext.getValues(claimFields.spanFootage) ?? '';
    const spanFootageDisplay = spanFootage.length > 0 ? spanFootage : localization.translate('dropdownOptions.na');

    // Return to damage details step
    const onStepClick = () => setStep(2);

    return (
        <div className={'flex flex-col'}>
            {/* STEP LINK */}
            <SummaryHeader isLink onLink={onStepClick}>
                {localization.translate('damageDetailsSummary.stepLink')}
            </SummaryHeader>

            <SummaryGroup>
                <SummarySection>
                    <SummaryGrid>
                        {/* DESCRIPTION */}
                        <SummaryText
                            label={localization.translate('damageDetailsSummary.damageDescriptionLabel')}
                            value={detailsOfDamageLoss}
                        />
                        <SummaryRow label={localization.translate('damageDetailsSummary.estimatedSubsAffectedLabel')}>
                            {subAffectedDisplay}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.outageDurationLabel')}>
                            {outageDurationDisplay}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.activityTypeLabel')}>
                            {activityType}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.damageByLabel')}>
                            {damageBy}
                        </SummaryRow>

                        {/* FACILITIES */}
                        <SummaryRow label={localization.translate('damageDetailsSummary.facilitiesLabel')}>
                            {facilities}
                        </SummaryRow>
                    </SummaryGrid>
                </SummarySection>

                <SummarySection>
                    <SummaryGrid>
                        {/* LOCATES */}
                        <SummaryRow
                            hideColon
                            label={localization.translate('damageDetailsSummary.wereLocatesPresentLabel')}
                        >
                            {hasLocateDetails}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.locateAccuracyLabel')}>
                            {locateAccuracy}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.locateMarkingsLabel')}>
                            {locateMarkingDisplay}
                        </SummaryRow>

                        {/* SPAN REPLACEMENT */}
                        <SummaryRow
                            hideColon
                            label={localization.translate('damageDetailsSummary.spanReplacementNeededLabel')}
                        >
                            {isSpanReplacement}
                        </SummaryRow>
                        <SummaryRow
                            hideColon
                            label={localization.translate('damageDetailsSummary.cableInConduitLabel')}
                        >
                            {isSpanUgInConduit}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.spanFootageLabel')}>
                            {spanFootageDisplay}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('damageDetailsSummary.cableSizeLabel')}>
                            {otherMaterial}
                        </SummaryRow>
                    </SummaryGrid>
                </SummarySection>
            </SummaryGroup>
        </div>
    );
};
